// frontend/src/services/WebSocketService.js

import WebSocketEvents from './WebSocketEvents';
import i18n from '../i18n';
import notificationSound from '../utils/notificationSound';
import {
  classifyWebSocketClose,
  getTokenSessionId,
  handleTerminalSessionFailure,
} from '../utils/authUtils';
import {
  createCanonicalLiveEtaPayload,
  isNoDataEtaPayload,
  hasEtaEnvelope,
  isEtaRetirement,
  shouldAcceptEtaUpdate
} from '../utils/liveEta';
import { createAuthenticatedWebSocket } from '../utils/webSocketAuth';
import logger from '../utils/logger';
import NotificationToastCloseButton from '../components/notifications/NotificationToastCloseButton';
import NotificationToastContent from '../components/notifications/NotificationToastContent';
import { WEBSOCKET_NOTIFICATION_CONTAINER_ID } from '../components/notifications/notificationToastConfig';
export const ETA_CLIENT_ADMISSION = Symbol('etaClientAdmission');
const MAX_IDLE_ETA_SCOPES = 128;

/**
 * Get the WebSocket base URL.
 * If VITE_WS_URL is set, use it directly.
 * Otherwise, derive from VITE_API_URL by converting http(s) to ws(s).
 */
const getWebSocketBaseUrl = () => {
  // If WS_URL is explicitly set, use it
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }

  // Derive from API URL
  const apiUrl = import.meta.env.VITE_API_URL || '';
  if (apiUrl.startsWith('https://')) {
    return apiUrl.replace('https://', 'wss://');
  }
  if (apiUrl.startsWith('http://')) {
    return apiUrl.replace('http://', 'ws://');
  }

  // Fallback: derive from current page location
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
};

class WebSocketService {
  constructor() {
    this.connections = new Map();
    this.reconnectAttempts = new Map();
    this.reconnectTimers = new Map();
    this.maxAttempts = 10;
    this.disconnectTimeouts = new Map();
    this.latestEtaData = new Map(); // Store latest ETA data for each connection
    this.etaAuthorities = new Map();
    this.etaGeneration = 0;
    this.latestWaypointStates = new Map(); // Store latest waypoint state per connection
    this.lastMessageAt = new Map();
    this.staleIntervals = new Map();
    this.STALE_CONNECTION_MS = 60000;
    this.STALE_CHECK_INTERVAL_MS = 15000;
    this.notificationEffectDedupCache = new Map();
    this.NOTIFICATION_EFFECT_DEDUP_TTL_MS = 10000;
    this.activeNotificationToast = null;
    this.pendingNotificationToast = null;
    this.notificationVisualSequence = 0;
  }

  getEtaAdmission(clientKey) {
    if (!this.etaAuthorities.has(clientKey)) {
      this.etaAuthorities.set(clientKey, { generation: ++this.etaGeneration,
        incarnation: null, publicationRevision: null, retired: new Set(), awaitingBaseline: true, denied: false, pending: false });
    }
    const state = this.etaAuthorities.get(clientKey);
    // HTTP-only readers have no socket disconnect to release their scope.
    // Bound idle scopes, retaining active subscriptions and invalidating late
    // HTTP completions by giving any recreated scope a fresh generation.
    this.etaAuthorities.delete(clientKey);
    this.etaAuthorities.set(clientKey, state);
    this.trimIdleEtaScopes();
    return state ? { generation: state.generation } : null;
  }

  trimIdleEtaScopes() {
    const idle = [...this.etaAuthorities.keys()].filter(key => !this.connections.has(key));
    for (const key of idle.slice(0, Math.max(0, idle.length - MAX_IDLE_ETA_SCOPES))) {
      this.etaAuthorities.delete(key);
      this.latestEtaData.delete(key);
    }
  }

  retireEtaData(clientKey, { awaitingBaseline = false, denied = false } = {}) {
    const previous = this.etaAuthorities.get(clientKey);
    this.etaAuthorities.set(clientKey, {
      generation: ++this.etaGeneration, incarnation: previous?.incarnation ?? null,
      publicationRevision: previous?.publicationRevision ?? null,
      retired: previous?.retired ?? new Set(), awaitingBaseline, denied, pending: !awaitingBaseline
    });
    this.latestEtaData.delete(clientKey);
    this.trimIdleEtaScopes();
    // This local barrier is delivered before domain/denial events so hooks and
    // frozen component snapshots cannot outlive the singleton's authority.
    this.broadcast(clientKey, { type: 'eta_local_retirement', payload: { denied } });
  }

  storeLatestEta(clientKey, etaData, receivedAtMs = Date.now(), { admission = null, bootstrap = false } = {}) {
    const state = this.etaAuthorities.get(clientKey);
    admission ??= etaData?.[ETA_CLIENT_ADMISSION];
    if (!state || state.denied || !hasEtaEnvelope(etaData)) return null;
    if (admission && admission.generation !== state.generation) return null;
    if (state.retired.has(etaData.incarnation)) return null;
    const retirement = isEtaRetirement(etaData);
    if (state.pending && !retirement) return null;
    // A reconnect clears display data, but preserves its revision watermark.
    // Snapshot-only recovery may publish an unavailable barrier at the same
    // raw revision; the server validates its newer snapshot before emission.
    if (state.incarnation === etaData.incarnation && state.publicationRevision !== null
        && etaData.publicationRevision < state.publicationRevision) return null;
    if (state.awaitingBaseline && !retirement && !bootstrap) return null;
    const changesIncarnation = state.incarnation && state.incarnation !== etaData.incarnation;
    if (changesIncarnation && !retirement && !(state.awaitingBaseline && bootstrap)) return null;
    const previousEta = this.latestEtaData.get(clientKey) || null;
    const accepted = shouldAcceptEtaUpdate({
      incomingEta: etaData,
      currentEta: previousEta,
      currentLiveEta: previousEta && !previousEta.cached && !isNoDataEtaPayload(previousEta)
        ? previousEta
        : null
    });
    if (!accepted) return null;

    if (retirement && (!previousEta || changesIncarnation
        || etaData.publicationRevision > previousEta.publicationRevision)) state.generation = ++this.etaGeneration;

    if (changesIncarnation) {
      state.retired.add(state.incarnation);
      // Retained only for the scoped connection; disconnect removes this map.
      while (state.retired.size > 32) state.retired.delete(state.retired.values().next().value);
    }
    state.incarnation = etaData.incarnation;
    state.publicationRevision = etaData.publicationRevision;
    state.awaitingBaseline = false;
    state.pending = false;

    // Store the canonical payload at the socket layer so every subscriber sees
    // the same countdown anchor. A no-data payload stays here as an ordering
    // watermark even though display consumers render it as absent ETA.
    const canonicalEta = createCanonicalLiveEtaPayload(etaData, previousEta, receivedAtMs);
    canonicalEta[ETA_CLIENT_ADMISSION] = { generation: state.generation };
    this.latestEtaData.set(clientKey, canonicalEta);
    return canonicalEta;
  }

  _cancelPendingReconnect(clientKey) {
    // Direct reopens must clear queued backoff work first or one logical reconnect can spawn two sockets.
    if (this.reconnectTimers.has(clientKey)) {
      clearTimeout(this.reconnectTimers.get(clientKey));
      this.reconnectTimers.delete(clientKey);
      logger.debug('Cancelled pending WebSocket reconnect timer', { clientKey });
    }
    this.reconnectAttempts.set(clientKey, 0);
  }

  // Reads the current token from localStorage and constructs the WebSocket URL.
  // JWT exp is no longer checked client-side because the database-backed
  // inactivity deadline is enforced by the server for HTTP and WebSockets.
  _createAuthenticatedWebSocket(clientKey, entityId, tripId) {
    const token = localStorage.getItem('token');
    if (!token) {
      logger.warn('WebSocket reconnection skipped - missing auth token', { clientKey });
      return null;
    }

    // Policy denial belongs to the database session, not one short-lived JWT
    // encoding of it. Normal token rotation preserves jti and must not unlock a
    // denied socket. Malformed legacy tokens fall back to exact bearer identity
    // so they cannot accidentally share a latch.
    const sessionIdentity = getTokenSessionId(token) || token;
    const denialKey = `${sessionIdentity}:${clientKey}`;
    const existingConnection = this.connections.get(clientKey);
    if (existingConnection?.denialKey === denialKey) {
      logger.debug('WebSocket reconnect suppressed by policy denial', { clientKey });
      return null;
    }

    const wsUrl = `${getWebSocketBaseUrl()}/ws/entities/${entityId}/trips/${tripId}`;
    const ws = createAuthenticatedWebSocket(wsUrl, token);
    // The close event belongs to the bearer used for this exact attempt, not
    // whichever token may be current when the event arrives later.
    ws.authBearer = token;
    return ws;
  }

  connect(entityId, tripId, tripData, onMessageCallback) {
    const clientKey = `entity_${entityId}_trip_${tripId}`;
    logger.debug('WebSocket connect request', { clientKey, entityId, tripId });

    const isWebSocketActive = this.isWebSocketActive(tripData);
    if (!isWebSocketActive) {
      logger.debug('WebSocket connection skipped - not active', { clientKey });
      return () => {};
    }
    
    // Clear any pending disconnect for this connection
    if (this.disconnectTimeouts.has(clientKey)) {
      clearTimeout(this.disconnectTimeouts.get(clientKey));
      this.disconnectTimeouts.delete(clientKey);
      logger.debug('Cancelled pending disconnect for WebSocket connection', { clientKey });
    }

    let connection = this.connections.get(clientKey);
    if (connection) {
      connection.subscribers.add(onMessageCallback);
      logger.debug('Added subscriber to existing WebSocket connection', { clientKey, totalSubscribers: connection.subscribers.size });
      
      // Send current status if already connected
      if (connection.status === 'connected') {
        setTimeout(() => {
          onMessageCallback({ type: 'connection_status', payload: { status: 'connected' } });
          
          // Send latest ETA data if available
          const latestEta = this.latestEtaData.get(clientKey);
          if (latestEta) {
            onMessageCallback({ type: 'eta_update', payload: latestEta });
          }
          const latestWaypointStates = this.latestWaypointStates.get(clientKey);
          if (latestWaypointStates) {
            latestWaypointStates.forEach((statePayload) => {
              onMessageCallback({ type: 'waypoint_state_change', payload: statePayload });
            });
          }
        }, 0);
      } else if (connection.status === 'access_denied') {
        // Late subscribers need the current terminal policy state even though
        // no socket will be reopened for this session/scope.
        setTimeout(() => {
          if (connection.subscribers.has(onMessageCallback)) {
            onMessageCallback({
              type: 'connection_status',
              payload: { status: 'access_denied' }
            });
          }
        }, 0);
      }
      
      // If WebSocket is closed, reopen it
      if (connection.ws.readyState === WebSocket.CLOSED || connection.ws.readyState === WebSocket.CLOSING) {
        logger.debug('Reopening closed WebSocket connection', { clientKey });

        this._cancelPendingReconnect(clientKey);

        // Reconnects may be the first activity after a quiet period; the server
        // validates the database inactivity deadline during the handshake.
        const ws = this._createAuthenticatedWebSocket(clientKey, entityId, tripId);
        if (!ws) {
          // The subscriber was already registered above. Always return its real
          // cleanup so a denial latch cannot retain an unmounted callback.
          return () => this.unsubscribe(clientKey, onMessageCallback);
        }
        connection.ws = ws;
        this._setupWebSocketHandlers(connection, clientKey, entityId, tripId);
      }
      
      return () => this.unsubscribe(clientKey, onMessageCallback);
    }
    
    // Create new connection
    
    // Initial trip sockets also need the same preflight because they read auth directly from localStorage.
    const ws = this._createAuthenticatedWebSocket(clientKey, entityId, tripId);
    if (!ws) {
      return () => {};
    }
    logger.debug('Creating new WebSocket connection', { clientKey });
    
    connection = {
      ws,
      subscribers: new Set([onMessageCallback]),
      isAlive: false,
      status: 'connecting',
      denialKey: null,
      _reconnectData: {
        entityId,
        tripId,
        tripData
      }
    };
    
    this.connections.set(clientKey, connection);
    this.reconnectAttempts.set(clientKey, 0);

    // Set up WebSocket handlers
    this._setupWebSocketHandlers(connection, clientKey, entityId, tripId);

    // Setup visibility change handling
    document.addEventListener('visibilitychange', this._handleVisibilityChange);

    return () => this.unsubscribe(clientKey, onMessageCallback);
  }

  _handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      logger.debug('Page hidden, pausing WebSocket connections', {});
      // Mark when the page was hidden for staleness detection
      this._pageHiddenTime = new Date();
    } else if (document.visibilityState === 'visible') {
      logger.debug('Page visible, checking WebSocket connections', {});
      
      // Calculate how long the page was hidden
      const hiddenDuration = this._pageHiddenTime ? (new Date() - this._pageHiddenTime) / 1000 : 0;
      logger.debug('Page backgrounding duration calculated', { hiddenDurationSeconds: hiddenDuration.toFixed(1) });
      
      this.connections.forEach((connection, clientKey) => {
        if (connection.ws.readyState === WebSocket.OPEN) {
          const lastMessageAt = this.lastMessageAt.get(clientKey);
          const staleAgeMs = lastMessageAt ? Date.now() - lastMessageAt : null;
          if (staleAgeMs !== null && staleAgeMs > this.STALE_CONNECTION_MS) {
            // Browsers can suspend timers and leave an OPEN socket that is no
            // longer receiving messages after foregrounding. Force a reconnect
            // so ETA and status streams are refreshed instead of trusting an
            // apparently open but silent connection.
            logger.warn('Stale WebSocket detected on visibility change, forcing reconnect', {
              clientKey,
              staleAgeMs
            });
            connection.status = 'stale_reconnecting';
            this.broadcast(clientKey, { type: 'connection_status', payload: { status: 'stale_reconnecting' } });
            connection.ws.close(4000, 'stale_connection');
            return;
          }
        }

        if (connection.ws.readyState === WebSocket.CLOSED || connection.ws.readyState === WebSocket.CLOSING) {
          logger.debug('Reopening WebSocket connection after page visibility change', { clientKey });
          
          const { entityId, tripId, tripData } = connection._reconnectData;
          
          if (this.isWebSocketActive(tripData)) {
            this._cancelPendingReconnect(clientKey);

            // Foreground resume may be the first activity after a long
            // background gap; server auth validates the inactivity deadline.
            const ws = this._createAuthenticatedWebSocket(clientKey, entityId, tripId);
            if (!ws) {
              return;
            }
            connection.ws = ws;
            this._setupWebSocketHandlers(connection, clientKey, entityId, tripId);
            
            // If page was hidden for more than 30 seconds, mark ETA data as potentially stale
            if (hiddenDuration > 30) {
              logger.debug('Long backgrounding detected, marking ETA data as stale', { clientKey, hiddenDurationSeconds: hiddenDuration });
              const etaDataKey = `${entityId}_trip_${tripId}`;
              if (this.latestEtaData.has(etaDataKey)) {
                const etaData = this.latestEtaData.get(etaDataKey);
                this.latestEtaData.set(etaDataKey, {
                  ...etaData,
                  _stale_after_resume: true,
                  _resume_timestamp: new Date().toISOString()
                });
              }
            }
          }
        }
      });
      
      this._pageHiddenTime = null;
    }
  }

  _setupWebSocketHandlers(connection, clientKey, entityId, tripId) {
    const thisWs = connection.ws;
    const ownsCurrentSocket = () => (
      this.connections.get(clientKey) === connection
      && connection.ws === thisWs
    );

    // A replacement attempt must not inherit keepalive work from the socket it
    // replaced. Heartbeat and stale detection restart only after this attempt
    // receives the server's database-backed authorization acknowledgement.
    this.stopHeartbeat(clientKey);
    this.stopStaleWatchdog(clientKey);
    connection.isAlive = false;

    connection.ws.onopen = () => {
      if (!ownsCurrentSocket()) return;
      // Browser onopen proves only that the transport handshake succeeded.
      // Endpoint authorization completes asynchronously on the server, so the
      // UI must remain connecting until the server sends `connected`.
      logger.debug('WebSocket transport opened; awaiting server authorization', {
        clientKey,
        entityId,
        tripId
      });
      connection.status = 'connecting';
      this.broadcast(clientKey, {
        type: 'connection_status',
        payload: { status: 'connecting' }
      });
    };

    connection.ws.onmessage = (event) => {
      if (!ownsCurrentSocket()) return;
      try {
        const messageStr = event.data;

        // Handle non-JSON messages
        if (messageStr === 'pong') {
          logger.debug('Received WebSocket pong response', { clientKey });
          connection.isAlive = true;
          this._markMessageReceived(clientKey);
          return;
        }

        const data = JSON.parse(messageStr);
        logger.debug('Received WebSocket message', { clientKey, messageType: data.type, hasPayload: !!data.payload });
        this._markMessageReceived(clientKey);

        if (data.type === 'connected') {
          this.retireEtaData(clientKey, { awaitingBaseline: true });
          connection.denialKey = null;
          connection.isAlive = true;
          connection.status = 'connected';
          this.reconnectAttempts.set(clientKey, 0);
          this.startHeartbeat(clientKey);
          this.startStaleWatchdog(clientKey);
        }

        // Handle notification messages (push notifications)
        if (data.type === 'notification' && data.payload) {
          void this.handleNotificationMessage(data, clientKey);
          // Also broadcast to subscribers in case they want to handle it differently
          this.broadcast(clientKey, { ...data, _fromWebSocket: true });
        }

        // Store latest ETA data before broadcasting so late subscribers and
        // current subscribers share one canonical client_received_at_ms anchor.
        if (data.type === 'eta_update' && data.payload) {
          const storedEta = this.storeLatestEta(clientKey, data.payload, Date.now(), {
            bootstrap: data.payload.event === 'initial_connection'
          });
          if (!storedEta) {
            logger.debug('Discarded stale ETA data from WebSocket', { clientKey });
            return;
          }
          data.payload = storedEta;
          logger.debug('Stored latest ETA data from WebSocket', { clientKey });
        }

        if (data.payload?.eta_status === 'projection_pending' || data.eta_status === 'projection_pending') {
          this.retireEtaData(clientKey);
        }

        // For status updates, emit a global event
        if (data.type === 'status_update' && data.payload) {

          const msgEntityId = data.payload.entityId || entityId;
          const msgTripId = data.payload.tripId || tripId;
          logger.debug('Emitting global status update from WebSocket', { entityId: msgEntityId, tripId: msgTripId, status: data.payload.status });

          WebSocketEvents.emitStatusUpdate(
            msgEntityId,
            msgTripId,
            data.payload.status,
            { archived_type: data.payload.archived_type }
          );
        }

        if (data.type === 'trip_delay_updated' && data.payload) {
          WebSocketEvents.emitTripDelayUpdated(
            data.payload.entityId || entityId,
            data.payload.tripId || tripId,
            data.payload
          );
        }

        // For survey reminder triggers, emit a global event
        if (data.type === 'survey_reminder_trigger' && data.payload) {
          const msgEntityId = data.payload.entityId || entityId;
          const msgTripId = data.payload.tripId || tripId;
          const eligibleRiderIds = data.payload.eligibleRiderIds || [];
          logger.debug('Emitting survey reminder trigger from WebSocket', {
            entityId: msgEntityId,
            tripId: msgTripId,
            eligibleRiderCount: eligibleRiderIds.length
          });

          WebSocketEvents.emitSurveyReminderTrigger(
            msgEntityId,
            msgTripId,
            eligibleRiderIds
          );
        }

        if (data.type === 'waypoint_state_change' && data.payload?.waypointId) {
          const existingStates = this.latestWaypointStates.get(clientKey) || new Map();
          existingStates.set(data.payload.waypointId, data.payload);
          this.latestWaypointStates.set(clientKey, existingStates);
        }

        // Broadcast to direct subscribers
        this.broadcast(clientKey, { ...data, _fromWebSocket: true });
      } catch (error) {
        logger.error('WebSocket message parsing error', { clientKey, error: error.message });
        connection.status = 'error';
        this.broadcast(clientKey, { type: 'connection_status', payload: { status: 'error', error: error.message } });
      }
    };

    connection.ws.onerror = (error) => {
      if (!ownsCurrentSocket()) return;
      logger.error('WebSocket connection error', { clientKey, error: error.message });
      connection.status = 'error';
      this.broadcast(clientKey, { type: 'connection_status', payload: { status: 'error', error: error.message } });
    };

    connection.ws.onclose = (event) => {
      // A direct reopen may replace connection.ws before the old socket finishes closing.
      if (!ownsCurrentSocket()) {
        logger.debug('Ignored onclose from superseded WebSocket', { clientKey, code: event.code });
        return;
      }

      logger.debug('WebSocket connection closed', { clientKey, code: event.code, reason: event.reason, wasClean: event.wasClean });
      connection.isAlive = false;
      const closeClassification = classifyWebSocketClose(event.code, event.reason);
      if (closeClassification.category === 'session_invalid') {
        this.retireEtaData(clientKey, { denied: true });
        logger.warn('WebSocket closed by server for invalid auth session', { clientKey });
        this.broadcast(clientKey, { type: 'connection_status', payload: { status: 'auth_expired' } });
        this.stopHeartbeat(clientKey);
        this.stopStaleWatchdog(clientKey);
        void handleTerminalSessionFailure({
          expectedToken: thisWs.authBearer,
          correlation: closeClassification.correlation,
        });
        return;
      }

      if (['static_denial', 'mutable_denial'].includes(closeClassification.category)) {
        this.retireEtaData(clientKey, { denied: true });
        const sessionIdentity = getTokenSessionId(thisWs.authBearer) || thisWs.authBearer;
        connection.denialKey = `${sessionIdentity}:${clientKey}`;
        connection.status = 'access_denied';
        this.broadcast(clientKey, { type: 'connection_status', payload: { status: 'access_denied' } });
        this.stopHeartbeat(clientKey);
        this.stopStaleWatchdog(clientKey);
        return;
      }

      const isStaleClose = event.code === 4000;
      connection.status = event.code === 1000 ? 'disconnected' : (isStaleClose ? 'stale_reconnecting' : 'error');
      this.broadcast(clientKey, { type: 'connection_status', payload: { status: connection.status } });
      this.stopHeartbeat(clientKey);
      this.stopStaleWatchdog(clientKey);

      // Standard reconnect for non-page visibility related disconnects
      if (event.code !== 1000 && this.reconnectAttempts.get(clientKey) < this.maxAttempts) {
        const attempts = this.reconnectAttempts.get(clientKey) + 1;
        this.reconnectAttempts.set(clientKey, attempts);
        const delay = Math.min(3000 * Math.pow(2, attempts), 60000);
        logger.debug('Scheduling WebSocket reconnection', { clientKey, delayMs: delay, attempt: attempts, maxAttempts: this.maxAttempts });
        connection.status = isStaleClose ? 'stale_reconnecting' : 'reconnecting';
        this.broadcast(clientKey, { type: 'connection_status', payload: { status: connection.status } });
        
        const timeoutId = setTimeout(() => {
          this.reconnectTimers.delete(clientKey);
          if (this.connections.has(clientKey) && 
              this.connections.get(clientKey).subscribers.size > 0) {
            
            // Reconnects reuse the current token; the server enforces the
            // database inactivity deadline and returns an auth close if needed.
            const ws = this._createAuthenticatedWebSocket(clientKey, entityId, tripId);
            if (!ws) {
              return;
            }
            connection.ws = ws;
            this._setupWebSocketHandlers(connection, clientKey, entityId, tripId);
          }
        }, delay);
        this.reconnectTimers.set(clientKey, timeoutId);
      } else {
        logger.debug('WebSocket reconnection skipped', { clientKey, reason: 'intentional disconnect or max attempts reached' });
      }
    };
  }

  isWebSocketActive(tripData) {
    // if tripData is null, return false
    if (!tripData) {
      return false;
    }
    // if tripData is not null, check if the status is scheduled, dispatched, or in_progress. if it is, return true
    const status = tripData.status;
    if (!status || !['scheduled', 'dispatched', 'in_progress'].includes(status)) {
      return false;
    }
    // if the status is not scheduled, return true
    if (status !== 'scheduled') {
      return true;
    }
    // if the status is scheduled, check if the departure time and trip date are not null. if they are, return true if the departure time is in the future. if they are not, return false
    const departureTime = tripData.departureTime || tripData.departure_time;
    const tripDate = tripData.trip_date || tripData.tripDate;
    
    if (!departureTime || !tripDate) {
      return false;
    }
    
    let departure;
    try {
      departure = new Date(`${tripDate.split('T')[0]}T${departureTime}`);
    } catch (error) {
      logger.error('Error parsing trip departure time for WebSocket activation check', { tripDate, departureTime, error: error.message });
      return false;
    }
    
    const minutesUntilDeparture = (departure - new Date()) / 1000 / 60;
    return minutesUntilDeparture < 50060;
  }

  startHeartbeat(clientKey) {
    const connection = this.connections.get(clientKey);
    if (!connection) return;
    
    if (connection.heartbeat) {
      clearInterval(connection.heartbeat);
    }
    
    connection.heartbeat = setInterval(() => {
      if (connection.ws.readyState === WebSocket.OPEN) {
        connection.ws.send('ping');
        logger.debug('Sent WebSocket ping heartbeat', { clientKey });
      }
    }, 30000);
  }

  stopHeartbeat(clientKey) {
    const connection = this.connections.get(clientKey);
    if (connection && connection.heartbeat) {
      clearInterval(connection.heartbeat);
      connection.heartbeat = null;
      logger.debug('Stopped WebSocket heartbeat', { clientKey });
    }
  }

  startStaleWatchdog(clientKey) {
    if (this.staleIntervals.has(clientKey)) {
      clearInterval(this.staleIntervals.get(clientKey));
    }

    const interval = setInterval(() => {
      const connection = this.connections.get(clientKey);
      if (!connection || connection.ws.readyState !== WebSocket.OPEN) {
        return;
      }

      const lastMessageAt = this.lastMessageAt.get(clientKey);
      if (!lastMessageAt) {
        return;
      }

      const staleAgeMs = Date.now() - lastMessageAt;
      if (staleAgeMs > this.STALE_CONNECTION_MS) {
        logger.warn('Stale WebSocket detected, forcing reconnect', {
          clientKey,
          staleAgeMs
        });
        connection.status = 'stale_reconnecting';
        this.broadcast(clientKey, { type: 'connection_status', payload: { status: 'stale_reconnecting' } });
        connection.ws.close(4000, 'stale_connection');
      }
    }, this.STALE_CHECK_INTERVAL_MS);

    this.staleIntervals.set(clientKey, interval);
  }

  stopStaleWatchdog(clientKey) {
    if (this.staleIntervals.has(clientKey)) {
      clearInterval(this.staleIntervals.get(clientKey));
      this.staleIntervals.delete(clientKey);
    }
  }

  _markMessageReceived(clientKey) {
    this.lastMessageAt.set(clientKey, Date.now());
  }

  broadcast(clientKey, message) {
    const connection = this.connections.get(clientKey);
    if (!connection) return;
    
    connection.subscribers.forEach((callback) => {
      try {
        callback(message);
      } catch (error) {
        logger.error('WebSocket message callback error', { clientKey, error: error.message });
      }
    });
  }

  unsubscribe(clientKey, callback) {
    const connection = this.connections.get(clientKey);
    if (!connection) return;
    
    connection.subscribers.delete(callback);
    logger.debug('Unsubscribed WebSocket callback', { clientKey, remainingSubscribers: connection.subscribers.size });
    
    if (connection.subscribers.size === 0) {
      this.disconnectGracefully(clientKey, 'No subscribers');
    }
  }

  disconnectGracefully(clientKey, reason) {
    if (this.disconnectTimeouts.has(clientKey)) {
      clearTimeout(this.disconnectTimeouts.get(clientKey));
    }
    
    const timeoutId = setTimeout(() => {
      const connection = this.connections.get(clientKey);
      if (connection && connection.subscribers.size === 0) {
        this.disconnectNow(clientKey, reason);
      } else {
        logger.debug('Skipped WebSocket disconnect, connection has active subscribers', { clientKey });
      }
      this.disconnectTimeouts.delete(clientKey);
    }, 300);
    
    this.disconnectTimeouts.set(clientKey, timeoutId);
    logger.debug('Scheduled graceful WebSocket disconnect', { clientKey, delayMs: 300 });
  }

  disconnectNow(clientKey, reason) {
    const connection = this.connections.get(clientKey);
    if (!connection) return;
    const socket = connection.ws;

    this._cancelPendingReconnect(clientKey);
    if (this.disconnectTimeouts.has(clientKey)) {
      clearTimeout(this.disconnectTimeouts.get(clientKey));
      this.disconnectTimeouts.delete(clientKey);
    }
    this.stopHeartbeat(clientKey);
    this.stopStaleWatchdog(clientKey);

    // Invalidate ownership before close(). Some WebSocket implementations can
    // deliver close synchronously; detached callbacks must not schedule a
    // reconnect or mutate state after the final subscriber has gone away.
    connection.ws = null;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;

    if ([WebSocket.CONNECTING, WebSocket.OPEN].includes(socket.readyState)) {
      socket.close(1000, reason);
    }

    this.retireEtaData(clientKey, { awaitingBaseline: true });
    connection.status = 'disconnected';
    this.broadcast(clientKey, { type: 'connection_status', payload: { status: 'disconnected' } });
    this.connections.delete(clientKey);
    this.reconnectAttempts.delete(clientKey);

    // Clean up ETA data and staleness tracking when disconnecting
    this.etaAuthorities.delete(clientKey);
    this.latestEtaData.delete(clientKey);
    this.latestWaypointStates.delete(clientKey);
    this.lastMessageAt.delete(clientKey);

    logger.debug('WebSocket connection disconnected', { clientKey, reason });
  }

  getConnectionStatus(clientKey) {
    const connection = this.connections.get(clientKey);
    return connection ? connection.status : 'disconnected';
  }

  // Get the latest ETA data for a connection
  getLatestEta(clientKey) {
    return this.latestEtaData.get(clientKey) || null;
  }

  // Clear ETA data for a connection
  clearEtaData(clientKey) {
    this.latestEtaData.delete(clientKey);
    logger.debug('Cleared WebSocket ETA data', { clientKey });
  }

  sendLocationUpdate(clientKey, location) {
    const connection = this.connections.get(clientKey);
    if (connection && connection.ws.readyState === WebSocket.OPEN) {
      connection.ws.send(JSON.stringify({
        type: 'location_update',
        payload: location,
      }));
      logger.debug('Sent WebSocket location update', {
        clientKey,
        hasLatitude: Number.isFinite(Number(location?.latitude)),
        hasLongitude: Number.isFinite(Number(location?.longitude))
      });
    }
  }

  sendEtaUpdate(clientKey, etaData) {
    if (etaData?.cached) {
      logger.debug('Skipped WebSocket ETA update for cached payload', { clientKey });
      return;
    }
    const connection = this.connections.get(clientKey);
    if (connection && connection.ws.readyState === WebSocket.OPEN) {
      // Store the ETA data locally as well
      if (!this.storeLatestEta(clientKey, etaData)) return;
      
      connection.ws.send(JSON.stringify({
        type: 'eta_update',
        payload: etaData,
      }));
      logger.debug('Sent WebSocket ETA update', { clientKey, hasEtaData: !!etaData });
    }
  }

  sendStatusUpdate(clientKey, status) {
    const connection = this.connections.get(clientKey);
    if (connection && connection.ws.readyState === WebSocket.OPEN) {
      connection.ws.send(JSON.stringify({
        type: 'status_update',
        payload: { status },
      }));
      logger.debug('Sent WebSocket status update', { clientKey, status });
    }
  }

  getEntityIdFromConnectionKey(clientKey) {
    const match = clientKey.match(/entity_(\d+)_trip_\d+/);
    return match ? match[1] : null;
  }
  
  getTripIdFromConnectionKey(clientKey) {
    const match = clientKey.match(/entity_\d+_trip_(\d+)/);
    return match ? match[1] : null;
  }

  buildNotificationDedupKey(payload = {}) {
    if (payload.notificationId) {
      return payload.notificationId;
    }

    if (payload.data?.notificationId) {
      return payload.data.notificationId;
    }

    return [
      payload.data?.eventType || '',
      payload.data?.tripId || '',
      payload.data?.bookingId || '',
      payload.severity || 'info',
      payload.title || payload.titleKey || '',
      payload.message || payload.messageKey || '',
      payload.messageParams ? JSON.stringify(payload.messageParams) : ''
    ].join('::');
  }

  resolveNotificationText(text, key, params, fallback) {
    return this.resolveNotificationTextWithSource(text, key, params, fallback).text;
  }

  resolveNotificationTextWithSource(text, key, params, fallback) {
    if (typeof text === 'string' && text.trim()) {
      return { text, source: 'payload' };
    }

    if (typeof key === 'string' && key.trim() && i18n.exists(key)) {
      return { text: i18n.t(key, params || {}), source: 'translation-key' };
    }

    return { text: fallback, source: 'local-fallback' };
  }

  buildNotificationToastContent(titleResolution, message, visualRecord, toast) {
    // A backend broadcast may supply the literal "Notification" itself. Only
    // hide the fallback created in this browser; text alone has no provenance.
    const displayTitle = titleResolution.source !== 'local-fallback'
      && typeof titleResolution.text === 'string'
      && titleResolution.text.trim()
      ? titleResolution.text.trim()
      : null;

    return (
      <NotificationToastContent
        title={displayTitle}
        message={message}
        visualToken={visualRecord.visualToken}
        onOverflow={(visualToken) => {
          this._handleNotificationToastOverflow(visualRecord, visualToken, toast);
        }}
      />
    );
  }

  clearNotificationVisualState() {
    // Authentication loss discards both Toastify's transient queue and the
    // service-owned priority records so a later login cannot inherit notices.
    const activeToast = this.activeNotificationToast;
    const pendingToast = this.pendingNotificationToast;
    const toastApi = pendingToast?.toastApi || activeToast?.toastApi;
    toastApi?.clearWaitingQueue({
      containerId: WEBSOCKET_NOTIFICATION_CONTAINER_ID
    });
    if (activeToast?.toastApi) {
      activeToast.toastApi.dismiss({
        id: activeToast.toastId,
        containerId: WEBSOCKET_NOTIFICATION_CONTAINER_ID
      });
    }
    this.notificationEffectDedupCache.clear();
    this.activeNotificationToast = null;
    this.pendingNotificationToast = null;
  }

  _completeNotificationVisualRecord(visualRecord) {
    if (!visualRecord || visualRecord.status === 'completed') return;

    if (this.activeNotificationToast === visualRecord) {
      this.activeNotificationToast = null;
    }
    if (this.pendingNotificationToast === visualRecord) {
      this.pendingNotificationToast = null;
    }

    visualRecord.status = 'completed';
    const { dedupKey, dedupReservation } = visualRecord;
    if (this.notificationEffectDedupCache.get(dedupKey) === dedupReservation) {
      dedupReservation.status = 'completed';
      dedupReservation.timestamp = Date.now();
    }
  }

  _handleNotificationVisualOpen(visualRecord) {
    if (visualRecord.status === 'completed') return;

    if (this.pendingNotificationToast === visualRecord) {
      this.pendingNotificationToast = null;
    }
    this.activeNotificationToast = visualRecord;
    visualRecord.status = 'active';
    if (
      this.notificationEffectDedupCache.get(visualRecord.dedupKey)
      === visualRecord.dedupReservation
    ) {
      visualRecord.dedupReservation.status = 'visual-active';
    }
  }

  _handleNotificationToastOverflow(visualRecord, visualToken, toast) {
    if (
      visualRecord.visualToken !== visualToken
      || visualRecord.manualDismiss
      || this.activeNotificationToast !== visualRecord
      || this.notificationEffectDedupCache.get(visualRecord.dedupKey)
        !== visualRecord.dedupReservation
      || !toast.isActive(
        visualRecord.toastId,
        WEBSOCKET_NOTIFICATION_CONTAINER_ID
      )
    ) {
      return false;
    }

    // Mark before update because Toastify remounts updated content. A remounted
    // observer must not schedule another update for the same visual instance.
    visualRecord.manualDismiss = true;
    toast.update(visualRecord.toastId, {
      containerId: WEBSOCKET_NOTIFICATION_CONTAINER_ID,
      autoClose: false,
      // Toastify v11 otherwise delays updates by 100 ms, which can revive a
      // visual that an important notification preempted in the meantime.
      delay: null
    });
    return true;
  }

  _publishNotificationToast(toast, severity, toastContent, options, visualRecord) {
    const importantVisual = this.activeNotificationToast?.isImportant
      ? this.activeNotificationToast
      : this.pendingNotificationToast?.isImportant
        ? this.pendingNotificationToast
        : null;

    // Routine visual feedback must not displace an actionable important notice.
    // Its sound and other applicable side effects have already run.
    if (!visualRecord.isImportant && importantVisual) {
      this._completeNotificationVisualRecord(visualRecord);
      return false;
    }

    if (this.pendingNotificationToast) {
      const replacedPending = this.pendingNotificationToast;
      toast.clearWaitingQueue({
        containerId: WEBSOCKET_NOTIFICATION_CONTAINER_ID
      });
      this._completeNotificationVisualRecord(replacedPending);
    }

    if (this.activeNotificationToast) {
      toast.dismiss({
        id: this.activeNotificationToast.toastId,
        containerId: WEBSOCKET_NOTIFICATION_CONTAINER_ID
      });
    }

    const toastMethod = severity === 'success'
      ? toast.success
      : severity === 'warning'
        ? toast.warning
        : severity === 'error'
          ? toast.error
          : toast.info;

    toastMethod(toastContent, options);
    if (this.activeNotificationToast !== visualRecord) {
      this.pendingNotificationToast = visualRecord;
      visualRecord.status = 'pending';
      visualRecord.dedupReservation.status = 'visual-pending';
    }
    return true;
  }

  shouldSuppressNotificationEffects(dedupKey) {
    const now = Date.now();
    // The backend already deduplicates delivery, but one browser tab can receive
    // the same notification through reconnect replay and active WS delivery.
    // Suppress only local side effects (toast/sound/browser notification), not
    // the underlying WebSocket event broadcast.
    for (const [key, entry] of this.notificationEffectDedupCache.entries()) {
      if (entry?.status === 'visual-active' || entry?.status === 'visual-pending') {
        continue;
      }
      const timestamp = typeof entry === 'number' ? entry : entry?.timestamp;
      if (!Number.isFinite(timestamp) || now - timestamp > this.NOTIFICATION_EFFECT_DEDUP_TTL_MS) {
        this.notificationEffectDedupCache.delete(key);
      }
    }

    const existingEntry = this.notificationEffectDedupCache.get(dedupKey);
    if (existingEntry?.status === 'visual-active' || existingEntry?.status === 'visual-pending') {
      return true;
    }
    const lastSeenAt = typeof existingEntry === 'number'
      ? existingEntry
      : existingEntry?.timestamp;
    return Number.isFinite(lastSeenAt) && (now - lastSeenAt) < this.NOTIFICATION_EFFECT_DEDUP_TTL_MS;
  }

  async handleNotificationMessage(data, clientKey) {
    const payload = data?.payload;
    if (!payload) {
      return false;
    }

    logger.debug('Received push notification via WebSocket', {
      clientKey,
      severity: payload.severity,
      eventType: payload.data?.eventType
    });

    const dedupKey = this.buildNotificationDedupKey(payload);
    if (this.shouldSuppressNotificationEffects(dedupKey)) {
      logger.debug('Suppressed duplicate notification side effects', {
        clientKey,
        hasDedupKey: Boolean(dedupKey),
        eventType: payload.data?.eventType
      });
      return false;
    }

    const dedupReservation = {
      status: 'pending',
      timestamp: Date.now()
    };
    this.notificationEffectDedupCache.set(dedupKey, dedupReservation);

    const { toast } = await import('react-toastify');
    const severity = payload.severity || 'info';
    const titleResolution = this.resolveNotificationTextWithSource(
      payload.title,
      payload.titleKey,
      payload.titleParams,
      'Notification'
    );
    const title = titleResolution.text;
    const message = this.resolveNotificationText(
      payload.message,
      payload.messageKey,
      payload.messageParams,
      'New notification'
    );
    const eventType = payload.data?.eventType || '';

    // Important notification types that warrant persistence
    // Scoped to specific user-actionable events, not all errors
    const importantEventTypes = [
      'trip_approaching_pickup',
      'trip_approaching_dropoff',
      'pickup_arrived',
      'dropoff_arrived'
    ];
    const isImportant = importantEventTypes.some(type => eventType.includes(type));

    const visualRecord = {
      visualToken: ++this.notificationVisualSequence,
      toastId: dedupKey,
      dedupKey,
      dedupReservation,
      toastApi: toast,
      isImportant,
      manualDismiss: false,
      status: 'candidate'
    };
    dedupReservation.status = 'visual-pending';

    // Toast duration: 30 seconds for important (with manual dismiss), 10 seconds for others
    const options = {
      containerId: WEBSOCKET_NOTIFICATION_CONTAINER_ID,
      position: 'top-center',
      autoClose: isImportant ? 30000 : 10000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: 'mouse',
      toastId: dedupKey,
      className: 'shuttle-notification-toast',
      closeButton: <NotificationToastCloseButton ariaLabel={i18n.t('close')} />,
      onOpen: () => this._handleNotificationVisualOpen(visualRecord),
      onClose: () => this._completeNotificationVisualRecord(visualRecord)
    };
    const toastContent = this.buildNotificationToastContent(
      titleResolution,
      message,
      visualRecord,
      toast
    );

    // Play notification sound once per logical notification in this browser context.
    try {
      if (eventType.includes('approaching')) {
        notificationSound.playImportant();
      } else {
        notificationSound.play(severity);
      }
    } catch (error) {
      logger.error('Failed to play notification sound:', error);
    }

    // Show native browser notification once per logical notification in this browser context.
    // Only if permission already granted (don't prompt automatically - let user opt-in via settings)
    // Only show native notification when tab is NOT focused to avoid double notification
    if (isImportant && 'Notification' in window && Notification.permission === 'granted') {
      const isTabHidden = document.hidden || document.visibilityState === 'hidden';
      const isWindowFocused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;

      if (isTabHidden || !isWindowFocused) {
        try {
          // Check if requireInteraction is supported (not on all mobile browsers)
          const notificationOptions = {
            body: message,
            icon: '/logo192.png',
            tag: dedupKey,
            silent: true // We already play our own sound
          };

          // Only add requireInteraction if likely supported (desktop browsers)
          const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
          if (!isMobile) {
            notificationOptions.requireInteraction = true;
          }

          const notification = new Notification(title, notificationOptions);

          // Auto-close after 2 minutes for mobile, 5 minutes for desktop
          const autoCloseTime = isMobile ? 120000 : 300000;
          setTimeout(() => notification.close(), autoCloseTime);

          // Handle click on notification
          notification.onclick = () => {
            window.focus();
            notification.close();
          };
        } catch (error) {
          logger.error('Failed to show native notification:', error);
        }
      }
    }

    // Display at most one WebSocket notification visual. Important events
    // preempt routine notices; routine events coalesce to the newest state.
    this._publishNotificationToast(toast, severity, toastContent, options, visualRecord);

    return true;
  }

  // ============= Browser Notification Permission Helpers =============

  /**
   * Check if browser notifications are supported
   * @returns {boolean} True if Web Notifications API is available
   */
  static isBrowserNotificationSupported() {
    return 'Notification' in window;
  }

  /**
   * Get current browser notification permission status
   * @returns {string} 'granted', 'denied', 'default', or 'unsupported'
   */
  static getBrowserNotificationPermission() {
    if (!WebSocketService.isBrowserNotificationSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  /**
   * Request browser notification permission from user
   * Should be called from a user interaction (button click) for best results
   * @returns {Promise<string>} Permission status after request
   */
  static async requestBrowserNotificationPermission() {
    if (!WebSocketService.isBrowserNotificationSupported()) {
      logger.warn('Browser notifications not supported');
      return 'unsupported';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      logger.warn('Browser notifications were previously denied by user');
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      logger.debug('Browser notification permission result:', permission);
      return permission;
    } catch (error) {
      logger.error('Error requesting notification permission:', error);
      return 'error';
    }
  }

  /**
   * Send a test browser notification to verify it works
   * @param {string} title - Notification title
   * @param {string} body - Notification body
   * @returns {boolean} True if notification was shown successfully
   */
  static sendTestBrowserNotification(title = 'Test Notification', body = 'Browser notifications are working!') {
    if (!WebSocketService.isBrowserNotificationSupported()) {
      return false;
    }

    if (Notification.permission !== 'granted') {
      logger.warn('Cannot send test notification - permission not granted');
      return false;
    }

    try {
      const notification = new Notification(title, {
        body,
        icon: '/logo192.png',
        tag: 'test-notification'
      });

      setTimeout(() => notification.close(), 5000);
      return true;
    } catch (error) {
      logger.error('Failed to send test notification:', error);
      return false;
    }
  }
}

const webSocketService = new WebSocketService();
export default webSocketService;

// Export static methods for use without instance
export const {
  isBrowserNotificationSupported,
  getBrowserNotificationPermission,
  requestBrowserNotificationPermission,
  sendTestBrowserNotification
} = WebSocketService;
